/**
 * History utility functions
 * 
 * Provides utilities for processing and organizing search history items.
 * Handles date calculations, period grouping, and text truncation.
 */

import type { HistoryItem } from '../components/MatchmakingHistoryPopover';

/**
 * Calculates whether a history item is from today or older
 * 
 * Compares the item's creation date (UTC) with today's date to determine
 * if it should be grouped under "today" or "older" sections.
 * 
 * @param createdAtUtc - UTC timestamp string of when item was created
 * @returns 'today' if created today, 'older' otherwise
 */
export const calculatePeriod = (createdAtUtc: string | undefined): 'today' | 'older' => {
  if (!createdAtUtc) {
    return 'older';
  }

  try {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayTimestamp = today.getTime();

    const itemDate = new Date(createdAtUtc);
    itemDate.setUTCHours(0, 0, 0, 0);
    const itemTimestamp = itemDate.getTime();

    return itemTimestamp >= todayTimestamp ? 'today' : 'older';
  } catch (error) {
    console.warn('Failed to parse createdAtUtc:', createdAtUtc, error);
    return 'older';
  }
};

/**
 * Processes history items by adding period information
 * 
 * Maps over history items and adds a 'period' field ('today' or 'older')
 * based on the creation date.
 * 
 * @param historyArray - Array of history items to process
 * @returns Array of history items with period field added
 */
export const processHistoryItems = (historyArray: HistoryItem[]): HistoryItem[] => {
  return historyArray.map((item) => {
    const calculatedPeriod = calculatePeriod(item.createdAtUtc);
    return {
      ...item,
      period: calculatedPeriod,
    };
  });
};

/**
 * Groups history items by period (today vs older)
 * 
 * Separates history items into two arrays based on their period field.
 * Used for displaying history in separate sections in the UI.
 * 
 * @param history - Array of history items with period field
 * @returns Object with todayItems and olderItems arrays
 */
export const groupHistoryByPeriod = (history: HistoryItem[]) => {
  const todayItems = history.filter((item) => item.period === 'today');
  const olderItems = history.filter((item) => item.period === 'older');
  return { todayItems, olderItems };
};

/**
 * Truncates text to a maximum length with ellipsis
 * 
 * Used for displaying long text (like search prompts) in limited space.
 * 
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation (default: 60)
 * @returns Truncated text with '...' if longer than maxLength
 */
export const truncateText = (text: string, maxLength: number = 60): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

